import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  CapsuleGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DodecahedronGeometry,
  DynamicDrawUsage,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  Points,
  PointsMaterial,
  SphereGeometry,
  TorusGeometry,
  Vector3,
} from 'three';
import { COLORS } from '../config/colors';
import type { LevelDefinition } from '../config/levelConfig';
import type { RandomManager } from '../core/RandomManager';

const mat = (color: number, roughness = 0.78, metalness = 0.02) =>
  new MeshStandardMaterial({ color, roughness, metalness, flatShading: true });

const shadow = <T extends Mesh>(mesh: T): T => {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
};

export interface FloatingDebris {
  group: Group;
  speed: number;
  phase: number;
}

export class Environment {
  readonly group = new Group();
  readonly debris: FloatingDebris[] = [];
  private rain: Points | null = null;
  private readonly clouds: Group[] = [];
  private readonly random: RandomManager;

  constructor(random: RandomManager) {
    this.random = random;
    this.group.name = 'CartoonRiverEnvironment';
    this.createBanks();
    this.createBridge();
    this.createDock();
    this.createFurniture();
    this.createTrees();
    this.createHouses();
    this.createClouds();
  }

  private createBanks(): void {
    const grass = mat(COLORS.grass);
    const soil = mat(COLORS.soil);
    const nearBank = shadow(new Mesh(new BoxGeometry(170, 1.8, 32), grass));
    nearBank.position.set(0, -0.35, 27);
    this.group.add(nearBank);
    const farBank = shadow(new Mesh(new BoxGeometry(170, 1.8, 32), grass));
    farBank.position.set(0, -0.35, -27);
    this.group.add(farBank);

    const nearEdge = shadow(new Mesh(new BoxGeometry(170, 1.25, 3), soil));
    nearEdge.position.set(0, -0.6, 12.9);
    this.group.add(nearEdge);
    const farEdge = shadow(new Mesh(new BoxGeometry(170, 1.25, 3), soil));
    farEdge.position.set(0, -0.6, -12.9);
    this.group.add(farEdge);

    const path = new Mesh(new PlaneGeometry(120, 4), mat(0xe5c992));
    path.rotation.x = -Math.PI / 2;
    path.position.set(0, 0.58, 22);
    path.receiveShadow = true;
    this.group.add(path);

    for (let i = 0; i < 18; i += 1) {
      const stone = shadow(new Mesh(new DodecahedronGeometry(this.random.range(0.35, 0.8), 0), mat(COLORS.stone)));
      stone.scale.y = 0.55;
      stone.position.set(this.random.range(-65, 65), this.random.range(0.3, 0.65), this.random.pick([-14.1, 14.1]));
      stone.rotation.set(this.random.next(), this.random.next(), this.random.next());
      this.group.add(stone);
    }
  }

  private createBridge(): void {
    const bridge = new Group();
    bridge.name = 'StoneBridge';
    const stone = mat(0xbcb2a5);
    const darkStone = mat(0x8c8279);
    const deck = shadow(new Mesh(new BoxGeometry(9, 1.1, 32), stone));
    deck.position.y = 4.3;
    bridge.add(deck);
    [-10, 0, 10].forEach((z) => {
      const pier = shadow(new Mesh(new BoxGeometry(2.4, 5, 2.4), darkStone));
      pier.position.set(0, 1.8, z);
      bridge.add(pier);
    });
    [-3.6, 3.6].forEach((x) => {
      const rail = shadow(new Mesh(new BoxGeometry(0.35, 2.1, 31), mat(0xd2c7b7)));
      rail.position.set(x, 5.65, 0);
      bridge.add(rail);
      for (let z = -14; z <= 14; z += 3.5) {
        const post = shadow(new Mesh(new BoxGeometry(0.55, 1.3, 0.55), darkStone));
        post.position.set(x, 5.1, z);
        bridge.add(post);
      }
    });
    bridge.position.x = -22;
    this.group.add(bridge);
  }

  private createDock(): void {
    const wood = mat(0x9a6941);
    const dock = new Group();
    const deck = shadow(new Mesh(new BoxGeometry(8, 0.55, 8), wood));
    deck.position.set(12, 0.9, 11.5);
    dock.add(deck);
    for (const x of [8.5, 15.5]) {
      for (const z of [8, 15]) {
        const post = shadow(new Mesh(new CylinderGeometry(0.22, 0.28, 4, 8), mat(0x765034)));
        post.position.set(x, -0.4, z);
        dock.add(post);
      }
    }
    const rescueBox = shadow(new Mesh(new BoxGeometry(3.5, 2.2, 2.2), mat(COLORS.coral)));
    rescueBox.position.set(16.5, 2, 18);
    dock.add(rescueBox);
    const crossH = new Mesh(new BoxGeometry(1.5, 0.38, 0.15), mat(COLORS.white));
    crossH.position.set(16.5, 2.1, 19.12);
    dock.add(crossH);
    const crossV = new Mesh(new BoxGeometry(0.38, 1.5, 0.15), mat(COLORS.white));
    crossV.position.set(16.5, 2.1, 19.13);
    dock.add(crossV);
    this.group.add(dock);
  }

  private createFurniture(): void {
    const bench = new Group();
    const wood = mat(0xb67246);
    const metal = mat(0x345263, 0.48, 0.4);
    const seat = shadow(new Mesh(new BoxGeometry(4.5, 0.35, 1.5), wood));
    seat.position.y = 1.3;
    bench.add(seat);
    const back = shadow(new Mesh(new BoxGeometry(4.5, 1.5, 0.3), wood));
    back.position.set(0, 2, 0.55);
    bench.add(back);
    [-1.7, 1.7].forEach((x) => {
      const leg = shadow(new Mesh(new BoxGeometry(0.25, 1.3, 1), metal));
      leg.position.set(x, 0.65, 0);
      bench.add(leg);
    });
    bench.position.set(-7, 0, 23);
    this.group.add(bench);

    const warning = new Group();
    const pole = shadow(new Mesh(new CylinderGeometry(0.12, 0.14, 4, 8), metal));
    pole.position.y = 2;
    warning.add(pole);
    const sign = shadow(new Mesh(new BoxGeometry(3.5, 2.2, 0.28), mat(COLORS.warning)));
    sign.position.y = 4.2;
    sign.rotation.z = -0.04;
    warning.add(sign);
    const mark = new Mesh(new CapsuleGeometry(0.16, 0.75, 4, 8), mat(COLORS.ink));
    mark.position.set(0, 4.42, 0.18);
    warning.add(mark);
    const dot = new Mesh(new SphereGeometry(0.18, 8, 6), mat(COLORS.ink));
    dot.position.set(0, 3.65, 0.18);
    warning.add(dot);
    warning.position.set(25, 0, 19);
    this.group.add(warning);
  }

  private createTrees(): void {
    const count = 24;
    const trunks = new InstancedMesh(new CylinderGeometry(0.35, 0.55, 4, 7), mat(0x8c5f3d), count);
    const crowns = new InstancedMesh(new DodecahedronGeometry(2.3, 0), mat(0x4ca963), count);
    trunks.castShadow = true;
    crowns.castShadow = true;
    const matrix = new Matrix4();
    for (let i = 0; i < count; i += 1) {
      const side = i % 2 === 0 ? 1 : -1;
      const x = -63 + i * 5.4 + this.random.range(-1.6, 1.6);
      const z = side * this.random.range(32, 39);
      const scale = this.random.range(0.78, 1.28);
      matrix.compose(new Vector3(x, 2.4 * scale, z), { x: 0, y: this.random.next() * Math.PI, z: 0, w: 1 } as never, new Vector3(scale, scale, scale));
      trunks.setMatrixAt(i, matrix);
      matrix.compose(new Vector3(x, 5.7 * scale, z), { x: 0, y: this.random.next() * Math.PI, z: 0, w: 1 } as never, new Vector3(scale, scale, scale));
      crowns.setMatrixAt(i, matrix);
    }
    trunks.instanceMatrix.needsUpdate = true;
    crowns.instanceMatrix.needsUpdate = true;
    this.group.add(trunks, crowns);
  }

  private createHouses(): void {
    [-48, -32, 36, 52].forEach((x, index) => {
      const house = new Group();
      const wall = shadow(new Mesh(new BoxGeometry(8, 5, 7), mat([0xf4b782, 0xf1d38b, 0xaacbea, 0xeaa2aa][index])));
      wall.position.y = 2.5;
      house.add(wall);
      const roof = shadow(new Mesh(new ConeGeometry(6, 3.3, 4), mat(0xb4544d)));
      roof.position.y = 6.6;
      roof.rotation.y = Math.PI / 4;
      house.add(roof);
      const door = new Mesh(new BoxGeometry(1.6, 2.6, 0.2), mat(0x65483b));
      door.position.set(0, 1.3, 3.6);
      house.add(door);
      house.position.set(x, 0.6, -38);
      this.group.add(house);
    });
  }

  private createClouds(): void {
    for (let i = 0; i < 7; i += 1) {
      const cloud = new Group();
      const cloudMaterial = mat(0xffffff);
      for (let j = 0; j < 4; j += 1) {
        const puff = new Mesh(new DodecahedronGeometry(2 + j * 0.25, 1), cloudMaterial);
        puff.scale.set(1.5, 0.75, 0.85);
        puff.position.set(j * 2.1, Math.sin(j) * 0.5, 0);
        cloud.add(puff);
      }
      cloud.position.set(this.random.range(-65, 65), this.random.range(21, 29), this.random.range(-52, -30));
      this.clouds.push(cloud);
      this.group.add(cloud);
    }
  }

  configure(level: LevelDefinition): void {
    this.debris.forEach(({ group }) => this.group.remove(group));
    this.debris.length = 0;
    if (this.rain) {
      this.group.remove(this.rain);
      this.rain.geometry.dispose();
      (this.rain.material as PointsMaterial).dispose();
      this.rain = null;
    }

    for (let i = 0; i < level.obstacles; i += 1) {
      const debris = new Group();
      const kind = i % 3;
      if (kind === 0) {
        const tire = new Mesh(new TorusGeometry(0.85, 0.25, 8, 16), mat(0x303640));
        tire.rotation.x = Math.PI / 2;
        debris.add(tire);
      } else if (kind === 1) {
        debris.add(shadow(new Mesh(new BoxGeometry(2.1, 1.2, 1.5), mat(0xa76e42))));
      } else {
        const branch = new Mesh(new CylinderGeometry(0.12, 0.24, 4, 7), mat(0x795036));
        branch.rotation.z = Math.PI / 2;
        debris.add(branch);
      }
      debris.position.set(this.random.range(-45, 45), 0.55, this.random.range(-8.5, 8.5));
      debris.rotation.y = this.random.next() * Math.PI;
      this.debris.push({ group: debris, speed: this.random.range(0.6, 1.15), phase: this.random.next() * Math.PI * 2 });
      this.group.add(debris);
    }

    if (level.storm) this.createRain();
  }

  private createRain(): void {
    const count = 560;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      positions[i * 3] = this.random.range(-70, 70);
      positions[i * 3 + 1] = this.random.range(2, 30);
      positions[i * 3 + 2] = this.random.range(-35, 35);
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3).setUsage(DynamicDrawUsage));
    const material = new PointsMaterial({ color: new Color(0xcdeeff), size: 0.18, transparent: true, opacity: 0.65 });
    this.rain = new Points(geometry, material);
    this.group.add(this.rain);
  }

  update(time: number, delta: number, riverSpeed: number, storm: boolean): void {
    this.clouds.forEach((cloud, index) => {
      cloud.position.x += delta * (0.35 + index * 0.025);
      if (cloud.position.x > 75) cloud.position.x = -75;
    });
    this.debris.forEach((item) => {
      item.group.position.x += delta * riverSpeed * item.speed * 2;
      item.group.position.y = 0.52 + Math.sin(time * 2 + item.phase) * 0.12;
      item.group.rotation.y += delta * 0.35;
      if (item.group.position.x > 72) item.group.position.x = -72;
    });
    if (storm && this.rain) {
      const positions = this.rain.geometry.getAttribute('position') as BufferAttribute;
      for (let i = 0; i < positions.count; i += 1) {
        const y = positions.getY(i) - delta * 20;
        positions.setY(i, y < 0 ? 30 : y);
        positions.setX(i, positions.getX(i) + delta * 1.4);
      }
      positions.needsUpdate = true;
    }
  }

  dispose(): void {
    this.group.traverse((object) => {
      if (object instanceof Mesh || object instanceof Points) {
        object.geometry.dispose();
        if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose());
        else object.material.dispose();
      }
    });
  }
}
